/**
 * useChunkErrorNotifier — detecta cuando el navegador falla al cargar
 * un chunk de JS que ya no existe en el server (típico tras un deploy
 * de la SPA con la pestaña del usuario aún abierta con un build viejo).
 *
 * Escenario:
 *   1. Usuario abre la app (build A, `index-XYZ.js` + N chunks).
 *   2. Hacemos deploy → Vite regenera todos los chunks con hashes nuevos.
 *   3. Usuario, con la pestaña vieja aún abierta, dispara una acción
 *      que hace `import("./algo")` (code-splitting dinámico).
 *   4. El bundle viejo referencia `algo-<hashViejo>.js` que ya no
 *      existe en el server. Nginx devuelve `index.html` como fallback
 *      SPA. El navegador rechaza el módulo por MIME incorrecto
 *      (text/html en vez de application/javascript).
 *   5. Se dispara `vite:preloadError` (event de Vite) + un `error`
 *      global si el usuario ya interactuó.
 *
 * Fix: mostrar una notification permanente con botón "Recargar" que
 * hace `location.reload()`. Es más suave que reload automático (que
 * podría interrumpir texto sin guardar) y más explícito que un toast
 * fugaz.
 *
 * Solo notificamos una vez por sesión (guard por ref) para que si
 * varios chunks fallan seguidos no se apilen notificaciones.
 */
import { useEffect, useRef } from 'react';
import { App, Button, notification } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import React from 'react';

type NotificationApi = ReturnType<typeof App.useApp>['notification'];

function isChunkLoadError(reason: unknown): boolean {
  if (!reason) return false;
  const msg = String(
    (reason as Error)?.message ?? reason,
  ).toLowerCase();
  return (
    msg.includes('dynamically imported module')
    || msg.includes('failed to fetch dynamically imported module')
    || msg.includes('chunkloaderror')
    || msg.includes('loading chunk')
    || (msg.includes('mime') && msg.includes('text/html'))
    || msg.includes('failed to import')
  );
}

function openReloadNotification(api: NotificationApi | typeof notification): void {
  const key = 'chunk-reload-notification';
  api.warning({
    key,
    message: 'Hay una versión nueva disponible',
    description:
      'La app se ha actualizado desde que abriste esta pestaña. '
      + 'Recarga para cargar la última versión — algunas acciones no '
      + 'funcionarán hasta que lo hagas.',
    duration: 0, // persistente hasta que el usuario la cierre o recargue
    placement: 'topRight',
    btn: React.createElement(
      Button,
      {
        type: 'primary',
        size: 'small',
        icon: React.createElement(ReloadOutlined),
        onClick: () => window.location.reload(),
      },
      'Recargar ahora',
    ),
  });
}

export function useChunkErrorNotifier(): void {
  const { notification: notificationApi } = App.useApp();
  const notified = useRef(false);

  useEffect(() => {
    // Nota: `notificationApi` puede ser undefined si el <App> aún no
    // ha renderizado por completo. Fallback a la API estática de
    // antd — en este caso concreto es aceptable (no depende del
    // theme del ConfigProvider — es solo una notification simple).
    const api = notificationApi ?? notification;

    const handle = (reason: unknown): void => {
      if (notified.current) return;
      if (!isChunkLoadError(reason)) return;
      notified.current = true;
      // eslint-disable-next-line no-console
      console.warn(
        '[chunk-error] Detected stale bundle after deploy. '
        + 'Prompted user to reload.',
        reason,
      );
      openReloadNotification(api);
    };

    // Vite dispara este evento específico para preload fails.
    const onVitePreloadError = (e: Event): void => {
      handle((e as CustomEvent).detail);
    };

    // Fallback para errores generales de JS que puedan ser de módulos.
    const onWindowError = (e: ErrorEvent): void => {
      handle(e.error ?? e.message);
    };

    // Fallback para promesas rechazadas (fetch de módulos falla como
    // promesa dinámica en algunos navegadores).
    const onUnhandledRejection = (e: PromiseRejectionEvent): void => {
      handle(e.reason);
    };

    window.addEventListener('vite:preloadError', onVitePreloadError);
    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('vite:preloadError', onVitePreloadError);
      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, [notificationApi]);
}
